<script setup lang="ts">
/**
 * SuperAdminToolbar — Debug toolbar visible only to super admins.
 *
 * Provides contextual CloudWatch deep-links, page context info,
 * and quick shortcuts to common log groups. Rendered at the bottom
 * of the viewport inside DefaultLayout, gated by isSuperAdmin.
 */

import { ref, onMounted } from 'vue'
import { useCloudWatchLinks } from '@/composables/useCloudWatchLinks'

const STORAGE_KEY = 'rws_admin_toolbar_expanded'

const { pageContext, logGroupLinks, insightsLink, shortcutLinks, entityIdLink, orgIdLink, hasLinks } = useCloudWatchLinks()

const isExpanded = ref(false)

onMounted(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    isExpanded.value = stored === 'true'
})

function toggle() {
    isExpanded.value = !isExpanded.value
    localStorage.setItem(STORAGE_KEY, String(isExpanded.value))
}

function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text)
}
</script>

<template>
    <!-- Collapsed tab -->
    <button
        v-if="!isExpanded"
        class="toolbar-tab"
        title="Open Super Admin Toolbar"
        aria-label="Open Super Admin Toolbar"
        @click="toggle"
    >
        <svg class="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 15l-2-2h4l-2 2z" fill="currentColor" stroke="none" />
            <path stroke-linecap="round" stroke-linejoin="round"
                d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
        </svg>
        <span class="tab-label">SA</span>
    </button>

    <!-- Expanded toolbar -->
    <div v-else class="toolbar">
        <div class="toolbar__header">
            <div class="toolbar__title">
                <svg class="toolbar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round"
                        d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
                </svg>
                Super Admin Toolbar
            </div>
            <button
                class="toolbar__close"
                title="Collapse toolbar"
                aria-label="Collapse Super Admin Toolbar"
                @click="toggle"
            >
                &times;
            </button>
        </div>

        <div class="toolbar__body">
            <!-- Page Context -->
            <div class="toolbar__section">
                <span class="section-label">Context</span>
                <div class="context-items">
                    <span class="context-chip" :title="pageContext.routeName">
                        <span class="chip-key">Route</span>
                        {{ pageContext.routeName }}
                    </span>
                    <!-- Entity ID: link to CloudWatch if available, copy-to-clipboard fallback -->
                    <a
                        v-if="pageContext.entityId && entityIdLink"
                        :href="entityIdLink"
                        class="context-chip context-chip--link"
                        :title="`Search all logs for ${pageContext.entityId}`"
                        :aria-label="`Search all logs for entity ${pageContext.entityId}`"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <span class="chip-key">ID</span>
                        {{ pageContext.entityId }}
                        <svg class="external-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                        </svg>
                    </a>
                    <span
                        v-else-if="pageContext.entityId"
                        class="context-chip context-chip--copyable"
                        title="Click to copy entity ID"
                        @click="copyToClipboard(pageContext.entityId!)"
                    >
                        <span class="chip-key">ID</span>
                        {{ pageContext.entityId }}
                    </span>
                    <!-- Org ID: link to CloudWatch if available, copy-to-clipboard fallback -->
                    <a
                        v-if="pageContext.organizationId && orgIdLink"
                        :href="orgIdLink"
                        class="context-chip context-chip--link"
                        :title="`Search all logs for org ${pageContext.organizationId}`"
                        :aria-label="`Search all logs for organization ${pageContext.organizationId}`"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <span class="chip-key">Org</span>
                        {{ pageContext.organizationId }}
                        <svg class="external-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                        </svg>
                    </a>
                    <span
                        v-else-if="pageContext.organizationId"
                        class="context-chip context-chip--copyable"
                        title="Click to copy organization ID"
                        @click="copyToClipboard(pageContext.organizationId!)"
                    >
                        <span class="chip-key">Org</span>
                        {{ pageContext.organizationId }}
                    </span>
                </div>
            </div>

            <!-- Divider -->
            <div v-if="hasLinks" class="toolbar__divider"></div>

            <!-- Log Group Links -->
            <div v-if="logGroupLinks.length" class="toolbar__section">
                <span class="section-label">Log Groups</span>
                <div class="link-items">
                    <a
                        v-for="link in logGroupLinks"
                        :key="link.label"
                        :href="link.url"
                        :title="link.description"
                        :aria-label="link.description"
                        class="toolbar-link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {{ link.label }}
                        <svg class="external-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                        </svg>
                    </a>
                </div>
            </div>

            <!-- Contextual Insights -->
            <template v-if="insightsLink">
                <div class="toolbar__divider"></div>
                <div class="toolbar__section">
                    <span class="section-label">Insights</span>
                    <div class="link-items">
                        <a
                            :href="insightsLink.url"
                            :title="insightsLink.description"
                            :aria-label="insightsLink.description"
                            class="toolbar-link toolbar-link--primary"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            🔍 {{ insightsLink.label }}
                            <svg class="external-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                            </svg>
                        </a>
                    </div>
                </div>
            </template>

            <!-- Shortcut Insights -->
            <template v-if="shortcutLinks.length">
                <div v-if="!insightsLink" class="toolbar__divider"></div>
                <div class="toolbar__section">
                    <span class="section-label">Shortcuts</span>
                    <div class="link-items">
                        <a
                            v-for="link in shortcutLinks"
                            :key="link.label"
                            :href="link.url"
                            :title="link.description"
                            :aria-label="link.description"
                            class="toolbar-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {{ link.label }}
                            <svg class="external-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                            </svg>
                        </a>
                    </div>
                </div>
            </template>

            <!-- No log groups configured message -->
            <div v-if="!hasLinks" class="toolbar__section">
                <span class="no-links-hint">
                    No log groups configured. Set <code>VITE_AWS_LOG_GROUP_*</code> env vars to enable CloudWatch links.
                </span>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* ── Collapsed Tab ────────────────────────────────── */
.toolbar-tab {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    z-index: var(--z-toast, 9000);
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    background: #1a1a2e;
    border: 1px solid #e2a830;
    border-radius: var(--radius-sm, 4px);
    color: #e2a830;
    font-family: var(--font-mono, 'JetBrains Mono', 'Fira Code', monospace);
    font-size: var(--text-xs, 0.6875rem);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.toolbar-tab:hover {
    background: #252545;
    border-color: #f0c040;
    box-shadow: 0 2px 12px rgba(226, 168, 48, 0.25);
}

.tab-icon {
    width: 14px;
    height: 14px;
}

.tab-label {
    letter-spacing: 0.05em;
}

/* ── Expanded Toolbar ─────────────────────────────── */
.toolbar {
    position: fixed;
    bottom: 0;
    left: var(--sidebar-width, 240px);
    right: 0;
    z-index: var(--z-toast, 9000);
    background: #1a1a2e;
    border-top: 2px solid #e2a830;
    font-family: var(--font-mono, 'JetBrains Mono', 'Fira Code', monospace);
    font-size: var(--text-xs, 0.6875rem);
    color: #c4c4d0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.35);
    transition: left var(--transition-slow, 0.3s);
}

/* Follow sidebar collapse */
:root.sidebar-collapsed .toolbar {
    left: var(--sidebar-collapsed-width, 64px);
}

.toolbar__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.875rem;
    border-bottom: 1px solid rgba(226, 168, 48, 0.2);
}

.toolbar__title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #e2a830;
    font-weight: 600;
    font-size: var(--text-xs, 0.6875rem);
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.toolbar__icon {
    width: 14px;
    height: 14px;
}

.toolbar__close {
    padding: 0.125rem 0.375rem;
    background: none;
    border: 1px solid transparent;
    border-radius: var(--radius-sm, 4px);
    color: #777;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    transition: all 0.15s;
}

.toolbar__close:hover {
    color: #e2a830;
    border-color: rgba(226, 168, 48, 0.3);
}

.toolbar__body {
    display: flex;
    align-items: center;
    gap: 0;
    padding: 0.375rem 0.875rem;
    overflow-x: auto;
}

.toolbar__section {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
}

.toolbar__divider {
    width: 1px;
    height: 20px;
    background: rgba(226, 168, 48, 0.25);
    margin: 0 0.75rem;
    flex-shrink: 0;
}

/* ── Section Labels ───────────────────────────────── */
.section-label {
    color: #888;
    font-size: 0.625rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    flex-shrink: 0;
}

/* ── Context Chips ────────────────────────────────── */
.context-items {
    display: flex;
    align-items: center;
    gap: 0.375rem;
}

.context-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.375rem;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 3px;
    color: #b0b0c0;
    font-size: 0.625rem;
}

.context-chip--copyable {
    cursor: pointer;
    transition: all 0.15s;
}

.context-chip--copyable:hover {
    background: rgba(226, 168, 48, 0.12);
    border-color: rgba(226, 168, 48, 0.3);
    color: #e2a830;
}

.context-chip--link {
    text-decoration: none;
    cursor: pointer;
    transition: all 0.15s;
    color: #87ceeb;
}

.context-chip--link:hover {
    background: rgba(135, 206, 235, 0.12);
    border-color: rgba(135, 206, 235, 0.3);
    color: #aee0f5;
}

.context-chip--link .external-icon {
    width: 9px;
    height: 9px;
    opacity: 0.5;
}

.context-chip--link:hover .external-icon {
    opacity: 1;
}

.chip-key {
    color: #666;
    font-weight: 500;
    margin-right: 0.125rem;
}

/* ── Links ────────────────────────────────────────── */
.link-items {
    display: flex;
    align-items: center;
    gap: 0.375rem;
}

.toolbar-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.5rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    color: #87ceeb;
    text-decoration: none;
    font-size: 0.625rem;
    font-weight: 500;
    transition: all 0.15s;
}

.toolbar-link:hover {
    background: rgba(135, 206, 235, 0.12);
    border-color: rgba(135, 206, 235, 0.3);
    color: #aee0f5;
}

.toolbar-link--primary {
    color: #e2a830;
    border-color: rgba(226, 168, 48, 0.25);
}

.toolbar-link--primary:hover {
    background: rgba(226, 168, 48, 0.12);
    border-color: rgba(226, 168, 48, 0.4);
    color: #f0c040;
}

.external-icon {
    width: 10px;
    height: 10px;
    opacity: 0.5;
}

.toolbar-link:hover .external-icon {
    opacity: 1;
}

/* ── Hint ─────────────────────────────────────────── */
.no-links-hint {
    color: #777;
    font-size: 0.625rem;
}

.no-links-hint code {
    padding: 0.0625rem 0.25rem;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 2px;
    color: #aaa;
    font-size: 0.5625rem;
}
</style>
